/**
 * Service for uploading files directly to S3 Storage.
 */
import supabase from './supabaseClient';

const BUCKET_NAME = 'intelliabucket';

export interface UploadFileParams {
  projectName: string;
  fileName: string;
  file: File;
  fileType?: 'uploads' | 'json' | 'generated' | 'data';
  onProgress?: (progress: number) => void;
}

export interface PresignedUrlResponse {
  signed_url: string;
  storage_path: string;
  expires_in: number;
}

class S3UploadService {
  /**
   * Get presigned URL for file upload from backend.
   */
  async getPresignedUrl(
    projectName: string,
    fileName: string,
    fileType: 'uploads' | 'json' | 'generated' | 'data' = 'uploads'
  ): Promise<PresignedUrlResponse> {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
    
    // Get session token for authentication
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${backendUrl}/api/storage/presigned-url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        project_name: projectName,
        file_name: fileName,
        file_type: fileType,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get presigned URL');
    }

    return response.json();
  }

  /**
   * Upload file directly to S3 Storage using presigned URL.
   */
  async uploadFile({
    projectName,
    fileName,
    file,
    fileType = 'uploads',
    onProgress,
  }: UploadFileParams): Promise<string> {
    try {
      // Get presigned URL from backend
      const { signed_url, storage_path } = await this.getPresignedUrl(
        projectName,
        fileName,
        fileType
      );

      // Upload file using presigned URL
      const xhr = new XMLHttpRequest();

      return new Promise((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onProgress) {
            const progress = (e.loaded / e.total) * 100;
            onProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200 || xhr.status === 201) {
            resolve(storage_path);
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.open('PUT', signed_url);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.send(file);
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  }

  /**
   * Upload multiple files with progress tracking.
   */
  async uploadFiles(
    files: File[],
    projectName: string,
    fileType: 'uploads' | 'json' | 'generated' | 'data' = 'uploads',
    onFileProgress?: (fileName: string, progress: number) => void
  ): Promise<string[]> {
    const uploadPromises = files.map((file) =>
      this.uploadFile({
        projectName,
        fileName: file.name,
        file,
        fileType,
        onProgress: (progress) => {
          if (onFileProgress) {
            onFileProgress(file.name, progress);
          }
        },
      })
    );

    return Promise.all(uploadPromises);
  }

  /**
   * Check if project exists.
   */
  async checkProjectExists(projectName: string): Promise<{ exists: boolean; sanitized_name: string }> {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
    
    // Get session token for authentication
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(`${backendUrl}/api/storage/check-project`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        project_name: projectName,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to check project');
    }

    return response.json();
  }
}

export const s3UploadService = new S3UploadService();
export default s3UploadService;

